import React from 'react';

function IntegratedChatContent() {
  return (
    <>
      <p>RepoPrompt's integrated chat feature allows you to interact with AI models directly within the application, eliminating the need to copy/paste to external services.</p>
      
      <h3>Setting Up API Access</h3>
      <p>Before using integrated chat, you need to configure your AI provider API keys:</p>
      <ol>
        <li>Open RepoPrompt's settings</li>
        <li>Navigate to the "API Keys" section</li>
        <li>Enter your API keys for the services you plan to use (OpenAI, Anthropic, etc.)</li>
        <li>API keys are stored securely in your system's keychain</li>
      </ol>
      
      <h3>Starting a Chat Session</h3>
      <ol>
        <li>Select the files and compose your instructions in the "Compose" tab</li>
        <li>Click the "New AI Chat" button at the bottom right of the window</li>
        <li>A new Chat panel opens with your prompt pre-filled</li>
        <li>Select your desired AI model from the dropdown (e.g., "GPT-4", "Claude 3")</li>
        <li>Click the "Send" button (arrow icon) to send your prompt to the AI</li>
      </ol>
      
      <h3>Working with Different Models</h3>
      <p>RepoPrompt allows you to select from various AI models with different strengths:</p>
      <ul>
        <li><strong>Task-based selection:</strong>
          <ul>
            <li>For detailed code reviews and complex edits: Claude 3.7 Sonnet, DeepSeek V3, or GPT-4o</li>
            <li>For quick iterations on smaller files: Gemini Flash 2.0 or Claude 3.5 Haiku</li>
            <li>For exploring ideas with no file changes: Any available model</li>
          </ul>
        </li>
        <li><strong>Edit format options:</strong>
          <ul>
            <li><strong>Diff Mode:</strong> The AI provides only the specific changes needed (requires compatible models)</li>
            <li><strong>Whole Mode:</strong> The AI rewrites entire files (works with all models)</li>
          </ul>
        </li>
        <li><strong>Temperature setting:</strong> 0 by default for optimal code editing accuracy. Can be raised for usecases requiring more creativity</li>
      </ul>
      
      <h3>Working with AI Responses</h3>
      <p>When the AI responds, RepoPrompt handles the reply intelligently:</p>
      <ul>
        <li>Normal text responses are displayed in the chat history</li>
        <li>Code snippets are syntax-highlighted for readability</li>
        <li>If the AI provides structured code changes (using RepoPrompt's expected format), they appear as distinct change blocks</li>
        <li>You can click on a change block to see the specific code diff</li>
      </ul>
      
      <h3>Applying Code Changes</h3>
      <ol>
        <li>When the AI suggests code changes, review them in the response</li>
        <li>Click the "Merge Changes" button to enter the review mode</li>
        <li>A diff review screen appears, showing the proposed modifications for each affected file</li>
        <li>Review the diffs carefully, ensuring they match your expectations</li>
        <li>Click "Accept All Files" to apply all changes, or review and accept individual files</li>
        <li>Click "Reset All Files" to discard all changes</li>
        <li>Click "Exit Review" when done to return to the chat</li>
      </ol>
      
      <h3>Continuing the Conversation</h3>
      <p>After receiving a response:</p>
      <ol>
        <li>Type your follow-up question or request in the chat input box</li>
        <li>Click "Send" to continue the conversation</li>
        <li>The AI maintains context from earlier messages in the conversation</li>
      </ol>
      
      <div className="tip-box">
        <strong>Tip:</strong> The integrated chat provides the most streamlined workflow for applying AI-suggested changes to your codebase, as it understands the XML diff format natively and provides a visual diff review interface.
      </div>
    </>
  );
}

export default IntegratedChatContent;